<script setup>
import { computed } from 'vue'
import { trans } from 'laravel-vue-i18n'

// ─── Props & emits ────────────────────────────────────────────────
const props = defineProps({
    reservations:   { type: Array, default: () => [] },
    visibleFormIds: { type: Set, default: () => new Set() },
    selectedDate:   Object, // moment
})

const emit = defineEmits(['select-month'])

const MONTH_NAMES = [
    trans('common.months_gen.jan'), trans('common.months_gen.feb'), trans('common.months_gen.mar'),
    trans('common.months_gen.apr'), trans('common.months_gen.may'), trans('common.months_gen.jun'),
    trans('common.months_gen.jul'), trans('common.months_gen.aug'), trans('common.months_gen.sep'),
    trans('common.months_gen.oct'), trans('common.months_gen.nov'), trans('common.months_gen.dec'),
]
const DOW_LETTERS = [
    trans('common.dow_short.mon'),
    trans('common.dow_short.tue'),
    trans('common.dow_short.wed'),
    trans('common.dow_short.thu'),
    trans('common.dow_short.fri'),
    trans('common.dow_short.sat'),
    trans('common.dow_short.sun'),
]

const today = new Date()

// ─── Computed ─────────────────────────────────────────────────────
const year = computed(() => props.selectedDate?.year() ?? today.getFullYear())

function isCurrentMonth(m) {
    return m === today.getMonth() && year.value === today.getFullYear()
}

function daysInMonth(m) {
    return new Date(year.value, m + 1, 0).getDate()
}

// Mon-based: Mon=0 … Sun=6
function firstDow(m) {
    return (new Date(year.value, m, 1).getDay() + 6) % 7
}

// Build grid cells: null for empty leading days, day number for real days
function monthCells(m) {
    const cells = []
    const blanks = firstDow(m)
    for (let i = 0; i < blanks; i++) cells.push(null)
    const days = daysInMonth(m)
    for (let d = 1; d <= days; d++) cells.push(d)
    // pad to full weeks
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
}

// Check if any visible reservation falls on year/month/day
const reservationDates = computed(() => {
    const set = new Set()
    props.reservations.forEach(r => {
        if (!props.visibleFormIds.has(r.id_reservation_forms)) return
        if (r.date) {
            set.add(r.date)
        } else if (r.date_start && r.date_end) {
            // mark every day in the range that belongs to this year
            const start = new Date(r.date_start)
            const end = new Date(r.date_end)
            const cur = new Date(start)
            while (cur <= end) {
                if (cur.getFullYear() === year.value) {
                    set.add(cur.toISOString().substring(0, 10))
                }
                cur.setDate(cur.getDate() + 1)
            }
        }
    })
    return set
})

function hasEvent(m, day) {
    if (!day) return false
    const dateStr = `${year.value}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return reservationDates.value.has(dateStr)
}

function isToday(m, day) {
    return day === today.getDate() && isCurrentMonth(m)
}

const months = computed(() =>
    Array.from({ length: 12 }, (_, i) => ({
        index: i,
        name: MONTH_NAMES[i],
        cells: monthCells(i),
    }))
)
</script>

<template>
    <div class="flex-1 min-h-0 overflow-y-auto bg-white dark:bg-neutral-900 px-6 py-5">

        <h2 class="text-[32px] font-semibold tracking-tight text-neutral-800 dark:text-neutral-100 mb-4">
            {{ year }}
        </h2>

        <div class="grid grid-cols-4 gap-4">
            <div
                v-for="m in months"
                :key="m.index"
                class="border rounded-xl p-3 cursor-pointer hover:border-blue-300 transition-colors"
                :class="isCurrentMonth(m.index)
                    ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/30'
                    : 'border-neutral-200 dark:border-neutral-700'"
                @click="emit('select-month', selectedDate.clone().month(m.index).date(1))"
            >
                <!-- Month name -->
                <div
                    class="text-[13px] font-semibold mb-2 capitalize"
                    :class="isCurrentMonth(m.index) ? 'text-blue-600' : 'text-neutral-700 dark:text-neutral-300'"
                >
                    {{ m.name }}
                </div>

                <!-- DOW header row -->
                <div class="grid grid-cols-7 gap-px mb-0.5">
                    <div
                        v-for="letter in DOW_LETTERS"
                        :key="letter + m.index"
                        class="text-center text-[9px] font-semibold text-neutral-300 dark:text-neutral-600 uppercase"
                    >
                        {{ letter }}
                    </div>
                </div>

                <!-- Day cells -->
                <div class="grid grid-cols-7 gap-px">
                    <div
                        v-for="(day, ci) in m.cells"
                        :key="ci"
                        class="flex flex-col items-center justify-center"
                        style="height: 24px"
                    >
                        <template v-if="day !== null">
                            <div
                                class="w-5 h-5 flex items-center justify-center rounded-full text-[10px] leading-none select-none"
                                :class="isToday(m.index, day)
                                    ? 'bg-blue-600 text-white font-semibold'
                                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                            >
                                {{ day }}
                            </div>
                            <!-- Event dot -->
                            <div
                                v-if="hasEvent(m.index, day) && !isToday(m.index, day)"
                                class="w-1 h-1 rounded-full bg-blue-500 mt-px"
                            />
                            <div v-else class="h-1"/>
                        </template>
                        <template v-else>
                            <div class="w-5 h-5"/>
                            <div class="h-1"/>
                        </template>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
